import keycloak from 'src/app/keycloak-init';
import { getCurrentUsername } from 'src/app/utils/manufacturing-batch-actor.util';

const GATEWAY_KEYCLOAK_CLIENT = 'sc-ms-mb-gateway';

/** Usuario legacy con permiso explícito (compatibilidad). */
export const ORG_TREE_LEGACY_EDITOR_USERNAMES = ['SADM009'];

export function canEditOrgTreeSupervisors(): boolean {
  if (keycloak.authenticated) return true;
  if (localStorage.getItem('token')) return true;

  const username = getCurrentUsername().toUpperCase();
  if (ORG_TREE_LEGACY_EDITOR_USERNAMES.includes(username)) return true;

  const roles =
    keycloak.tokenParsed?.resource_access?.[GATEWAY_KEYCLOAK_CLIENT]?.roles ?? [];
  return roles.includes('admin');
}

export interface OrgTreeEmployeeNode {
  nodeType?: 'employee';
  id: number;
  name: string;
  surname: string;
  email?: string;
  photo?: string;
  workareaId?: number;
  supervisorId?: number;
  orgSegmentLabel?: string | null;
  orgTreeRoot?: boolean;
  children: OrgTreeNode[];
}

export interface OrgTreeSegmentNode {
  nodeType: 'segment';
  label: string;
  children: OrgTreeNode[];
}

export type OrgTreeNode = OrgTreeEmployeeNode | OrgTreeSegmentNode;

export function isOrgTreeSegmentNode(node: OrgTreeNode): node is OrgTreeSegmentNode {
  return node.nodeType === 'segment';
}

export function isOrgTreeEmployeeNode(node: OrgTreeNode): node is OrgTreeEmployeeNode {
  return !isOrgTreeSegmentNode(node);
}

export function findOrgTreeEmployeeNode(nodes: OrgTreeNode[], employeeId: number): OrgTreeEmployeeNode | null {
  for (const node of nodes) {
    if (isOrgTreeEmployeeNode(node)) {
      if (node.id === employeeId) return node;
      const found = findOrgTreeEmployeeNode(node.children, employeeId);
      if (found) return found;
    } else {
      const found = findOrgTreeEmployeeNode(node.children, employeeId);
      if (found) return found;
    }
  }
  return null;
}

export function collectOrgTreeEmployeeIds(nodes: OrgTreeNode[]): Set<number> {
  const ids = new Set<number>();
  const walk = (list: OrgTreeNode[]): void => {
    for (const node of list) {
      if (isOrgTreeEmployeeNode(node)) {
        ids.add(node.id);
      }
      walk(node.children || []);
    }
  };
  walk(nodes);
  return ids;
}

export function isEmployeeInSubtree(nodes: OrgTreeNode[], employeeId: number): boolean {
  for (const node of nodes) {
    if (isOrgTreeEmployeeNode(node)) {
      if (node.id === employeeId) return true;
      if (isEmployeeInSubtree(node.children, employeeId)) return true;
    } else if (isEmployeeInSubtree(node.children, employeeId)) {
      return true;
    }
  }
  return false;
}

export function wouldCreateOrgCycle(
  tree: OrgTreeNode[],
  employeeId: number,
  newSupervisorId: number,
): boolean {
  if (employeeId === newSupervisorId) return true;
  const employeeNode = findOrgTreeEmployeeNode(tree, employeeId);
  if (!employeeNode) return false;
  return isEmployeeInSubtree(employeeNode.children, newSupervisorId);
}

export function mergePendingOrgSegments(
  nodes: OrgTreeNode[],
  pending: ReadonlyArray<{ supervisorId: number; label: string }>,
): OrgTreeNode[] {
  return nodes.map((node) => {
    if (isOrgTreeSegmentNode(node)) {
      return {
        ...node,
        children: mergePendingOrgSegments(node.children, pending),
      };
    }

    const mergedChildren = mergePendingOrgSegments(node.children, pending);
    const existingSegmentLabels = new Set(
      mergedChildren
        .filter(isOrgTreeSegmentNode)
        .map((segment) => segment.label.trim().toLowerCase()),
    );
    const extraSegments: OrgTreeSegmentNode[] = pending
      .filter(
        (entry) =>
          entry.supervisorId === node.id
          && !existingSegmentLabels.has(entry.label.trim().toLowerCase()),
      )
      .map((entry) => ({
        nodeType: 'segment',
        label: entry.label,
        children: [],
      }));

    return {
      ...node,
      children: [...mergedChildren, ...extraSegments],
    };
  });
}

export type OrgDragPayload =
  | { type: 'employee'; employeeId: number }
  | { type: 'virtual-segment'; label: string };

export type OrgDropTargetContext =
  | { type: 'employee'; employeeId: number }
  | { type: 'segment'; parentEmployeeId: number; label: string }
  | { type: 'root' };

export const ORG_ROOT_DROP_LIST_ID = 'org-drop-root';

export function employeeOrgDropListId(employeeId: number): string {
  return `org-drop-emp-${employeeId}`;
}

export function segmentOrgDropListId(parentEmployeeId: number, label: string): string {
  return `org-drop-seg-${parentEmployeeId}-${label.trim().toLowerCase().replace(/\s+/g, '-')}`;
}

export function collectOrgDropListIds(nodes: OrgTreeNode[]): string[] {
  const ids: string[] = [];
  const walk = (list: OrgTreeNode[], parentEmployeeId?: number): void => {
    for (const node of list) {
      if (isOrgTreeEmployeeNode(node)) {
        ids.push(employeeOrgDropListId(node.id));
        walk(node.children, node.id);
      } else if (parentEmployeeId != null) {
        ids.push(segmentOrgDropListId(parentEmployeeId, node.label));
        walk(node.children, parentEmployeeId);
      } else {
        walk(node.children, parentEmployeeId);
      }
    }
  };
  walk(nodes);
  return ids;
}

export function collectOrgEmployeeSubtreeIds(node: OrgTreeEmployeeNode): number[] {
  const ids = [node.id];
  const walk = (children: OrgTreeNode[]): void => {
    for (const child of children) {
      if (isOrgTreeEmployeeNode(child)) {
        ids.push(...collectOrgEmployeeSubtreeIds(child));
      } else {
        walk(child.children);
      }
    }
  };
  walk(node.children || []);
  return ids;
}

export function collectOrgSegmentSubtreeIds(segment: OrgTreeSegmentNode): number[] {
  const ids: number[] = [];
  for (const child of segment.children || []) {
    if (isOrgTreeEmployeeNode(child)) {
      ids.push(...collectOrgEmployeeSubtreeIds(child));
    } else {
      ids.push(...collectOrgSegmentSubtreeIds(child));
    }
  }
  return ids;
}

export type OrgNodeRemovePayload =
  | { type: 'employee'; employeeId: number }
  | { type: 'segment'; parentEmployeeId: number; label: string };
